import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Icon, Flex, Div } from 'ui';
import styled from 'styled-components';
import { Dropdown, Menu, Divider } from 'antd';
import Panel from 'common/panel';
import { alertSeverities, severities } from 'utils/app-configs';
import { SEVERITY_ERROR, SEVERITY_WARNING, SEVERITY_INFO } from '../containers/alert-constants';
import Tag from '../../gantt/components/block/components/block-tag';
import { blockConfig } from '../../gantt/config';

const Wrapper = styled(Div)`
  min-height: ${p => p.small ? '90px' : '130px'};
`;

export const Text = styled.div`
  color: ${p => p.theme.colors.alerts[p.severity]};
  font-weight: ${p => p.theme.fontWeight.bolder};
`;

const SeverityIcon = styled(Icon)`
  color: ${p => p.theme.colors.alerts[p.severity]};
`;

const AlertText = styled.span`
  font-size: ${p => p.fontSize}px;
  color: rgba(0, 0, 0, 0.65);
  ${p => p.largeTag && `
    line-height: 25px;
  `}
`;

const Alert = styled(Flex)`
  ${p => p.needsTitle && 'margin-top: 10px;'}
  margin-bottom: 10px;
`;

const AlertAction = styled.a`
  text-decoration: underline;
`;

const Actions = styled.span`
  margin-left: 5px;
  white-space:nowrap;
  text-transform: lowercase;
`;

const ActionBody = styled.div`
  font-size: ${p => p.fontSize}px;
  margin-top: -5px;
  padding-left: 5px;
  ${p => !p.showFullDescription &&
    `
      margin-top: 0px !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;

const StyledDivider = styled(Divider)`
  margin: 0 4px;
`;

const ShowAction = styled.p`
    text-decoration: underline;
    cursor: pointer;
    color: #38adf5;
`;

export const getIcon = (severity) => {
  if (severity === SEVERITY_INFO) {
    return <SeverityIcon type='info-circle' severity={severity} />;
  }
  if (severity === SEVERITY_WARNING) {
    return <SeverityIcon type='exclamation-triangle' severity={severity} />;
  }
  if (severity === SEVERITY_ERROR) {
    return <SeverityIcon type='exclamation-circle' severity={severity} />;
  }
  return null;
};

const Alerts = (props) => {
  const {
    alerts = [],
    small = false,
    maxVisibleAlerts = 4,
    snoozeRequest,
    discardRequest,
    isSaving,
    intl,
    blockAlerts,
    needsActions = true,
    showFullDescription = true,
    needsTitle = true,
    fontSize = 16,
    showIconOnly = false,
    largeTag = false,
    blockId = '',
    laneId = '',
    disabled = false,
    tagOnly = false,
  } = props;

  const [maxVisible, setMaxVisible] = useState(maxVisibleAlerts);
  const [collapsed, setCollapsed] = useState(true);

  useEffect(() => {
    if (!collapsed) {
      setMaxVisible(alerts.length);
    } else {
      setMaxVisible(maxVisibleAlerts);
    }
  }, [collapsed]);

  const handleClick = (item, a) => {
    if (!isSaving[a.id]) {
      if (item.key === 'day') {
        snoozeRequest({ id: a.id, seconds: 60 * 60 * 24, laneId, blockId });
      } else if (item.key === 'week') {
        snoozeRequest({ id: a.id, seconds: 60 * 60 * 24 * 7, laneId, blockId });
      } else if (item.key === 'month') {
        snoozeRequest({ id: a.id, seconds: 60 * 60 * 24 * 30, laneId, blockId });
      }
    }
  };

  const menu = a =>
    <Menu onClick={item => handleClick(item, a)}>
      <Menu.Item key='day'>{intl.formatMessage({ id: 'dashboard.alerts.SNOOZE_DAY' })}</Menu.Item>
      <Menu.Item key='week'>{intl.formatMessage({ id: 'dashboard.alerts.WEEK' })}</Menu.Item>
      <Menu.Item key='month'>{intl.formatMessage({ id: 'dashboard.alerts.MONTH' })}</Menu.Item>
    </Menu>;
  const getActionsButtons = a =>
    <Actions>
      <>
        <Dropdown
          overlay={() => menu(a)}
        >
          <AlertAction
            disabled={isSaving[a.id]}
            onClick={() => !isSaving[a.id] && snoozeRequest({
              id: a.id,
              seconds: 60 * 60 * 24,
              laneId,
              blockId,
            })}
          >
            <FormattedMessage id='dashboard.alerts.SNOOZE' />
            <Icon type='chevron-down' size='sm' />
          </AlertAction>
        </Dropdown>
      </>
      <StyledDivider type='vertical' />
      <AlertAction
        disabled={isSaving[a.id]}
        onClick={() => !isSaving[a.id] && discardRequest({ id: a.id, laneId, blockId })}
      >
        <FormattedMessage id='dashboard.alerts.DISCARD' />
      </AlertAction>
    </Actions>;

  // eslint-disable-next-line
  const getAlerts = (alertsArray) => {
    if (alertsArray.length) {
      return alertsArray
        ?.sort((a, b) => a.severity - b.severity)
        .slice(0, maxVisible)
        ?.map((a) => {
          if (a.severity !== 0) {
            return (
              <Alert key={a.id} needsTitle={needsTitle}>
                <Tag
                  color={alertSeverities[a.severity].colorStatus}
                  tagIcon={alertSeverities[a.severity].icon}
                  text={alertSeverities[a.severity].text}
                  showIconOnly={
                    showIconOnly &&
                    a.severity !== severities.OK &&
                    a.severity !== blockConfig.blockSeverities.SUCCESS
                  }
                  large={largeTag}
                />
                {!tagOnly && a.severity !== severities.OK &&
                  a.severity !== blockConfig.blockSeverities.SUCCESS ?
                  // eslint-disable-next-line
                  <ActionBody
                    showFullDescription={showFullDescription}
                    fontSize={fontSize}
                    largeTag={largeTag}
                  >
                    <AlertText fontSize={fontSize} largeTag={largeTag}>
                      {a.translatedName || a.name}
                    </AlertText>
                    {needsActions && !disabled ? getActionsButtons(a) : null}
                  </ActionBody> :
                  null
                }
              </Alert>
            );
          }
          return null;
        });
    }
    return (
      <Tag
        color={alertSeverities[severities.OK].colorStatus}
        tagIcon={alertSeverities[severities.OK].icon}
        text={alertSeverities[severities.OK].text}
        showIconOnly={false}
        large={largeTag}
      />
    );
  };

  return (
    needsTitle ?
      <Panel
        title={
          <Flex>
            <Div flex={1}>
              <FormattedMessage id='dashboard.alerts.TITLE' /> ({alerts.length})
            </Div>
          </Flex>
        }
      >
        <Wrapper small={small}>{getAlerts(blockAlerts || alerts)}</Wrapper>
        {alerts.length > maxVisibleAlerts ?
          <ShowAction onClick={() => setCollapsed(c => !c)}>
            {collapsed ?
              <FormattedMessage id='publishSteps.SHOW_MORE' /> :
              <FormattedMessage id='publishSteps.SHOW_LESS' />
            }
          </ShowAction> :
          null
        }
      </Panel> :
      getAlerts(blockAlerts || alerts)
  );
};

Alerts.propTypes = {
  maxVisibleAlerts: PropTypes.number,
  alerts: PropTypes.array,
  small: PropTypes.bool,
  intl: PropTypes.object,
  snoozeRequest: PropTypes.func,
  discardRequest: PropTypes.func,
  isSaving: PropTypes.bool,
  needsActions: PropTypes.bool,
  showFullDescription: PropTypes.bool,
  needsTitle: PropTypes.bool,
  blockAlerts: PropTypes.array,
  fontSize: PropTypes.number,
  showIconOnly: PropTypes.bool,
  largeTag: PropTypes.bool,
  blockId: PropTypes.string,
  laneId: PropTypes.string,
  disabled: PropTypes.bool,
  tagOnly: PropTypes.bool,
};

export default injectIntl(Alerts);
